import React, { useCallback, useState } from 'react'
import { AsyncSelect } from '@atlaskit/select'
import { token } from '@atlaskit/tokens'
import { searchJiraIssues } from '../../utils/jiraContext'

/**
 * JIRA ISSUE PICKER COMPONENT
 *
 * Autocomplete dropdown for searching and selecting Jira issues.
 * Uses Jira's issue picker API to search as the user types.
 *
 * Features:
 * - Debounced search (built into AsyncSelect)
 * - Shows issue type icon, key, and summary
 * - Loading spinner while fetching
 * - Keyboard navigation support
 * - Clear selection option
 *
 * @param {function} onSelect - Callback when an issue is selected (receives { key, summary })
 * @param {string} placeholder - Placeholder text for the input
 * @param {boolean} isDisabled - Whether the picker is disabled
 * @param {object} value - Currently selected value (for controlled component)
 * @param {function} onChange - Change handler for controlled component
 */
export function JiraIssuePicker({
  onSelect,
  placeholder = 'Search for a Jira issue...',
  isDisabled = false,
  value = null,
  onChange
}) {
  // Track if we're in a loading state (for better UX messaging)
  const [hasSearched, setHasSearched] = useState(false)

  /**
   * LOAD OPTIONS
   * Called by AsyncSelect when user types. Searches Jira issues.
   */
  const loadOptions = useCallback(async (inputValue) => {
    if (!inputValue || inputValue.length < 2) {
      return []
    }

    setHasSearched(true)

    try {
      const issues = await searchJiraIssues(inputValue)
      // Transform to react-select option format
      return issues.map(issue => ({
        value: issue.key,
        label: issue.key,
        summary: issue.summary,
        icon: issue.issueTypeIcon
      }))
    } catch (error) {
      console.error('Failed to search issues:', error)
      return []
    }
  }, [])

  /**
   * HANDLE CHANGE
   * Called when user selects an option.
   */
  const handleChange = useCallback((option) => {
    if (onChange) {
      onChange(option)
    }
    if (onSelect && option) {
      onSelect({
        key: option.value,
        summary: option.summary
      })
    }
  }, [onChange, onSelect])

  /**
   * CUSTOM OPTION COMPONENT
   * Renders each option with issue type icon, key, and summary.
   */
  const formatOptionLabel = useCallback((option, { context }) => {
    // In the menu, show full details
    if (context === 'menu') {
      return (
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: '10px',
            padding: '4px 0'
          }}
        >
          {/* Issue type icon */}
          {option.icon && (
            <img
              src={option.icon}
              alt="Issue"
              style={{
                width: '16px',
                height: '16px',
                flexShrink: 0,
                marginTop: '2px'
              }}
              onError={(e) => { e.target.style.display = 'none' }}
            />
          )}

          {/* Issue details */}
          <div style={{ flex: 1, minWidth: 0 }}>
            {/* Issue key */}
            <div
              style={{
                fontSize: '13px',
                fontWeight: 600,
                color: token('color.text', '#172B4D')
              }}
            >
              {option.label}
            </div>

            {/* Issue summary */}
            {option.summary && (
              <div
                style={{
                  fontSize: '12px',
                  color: token('color.text.subtle', '#6B778C'),
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  maxWidth: '100%',
                  marginTop: '2px'
                }}
              >
                {option.summary}
              </div>
            )}
          </div>
        </div>
      )
    }

    // In the value display (selected), show icon + key + summary
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        {option.icon && (
          <img
            src={option.icon}
            alt="Issue"
            style={{ width: '16px', height: '16px' }}
            onError={(e) => { e.target.style.display = 'none' }}
          />
        )}
        <span style={{ fontWeight: 500 }}>{option.label}</span>
        {option.summary && (
          <span
            style={{
              fontSize: '12px',
              color: token('color.text.subtle', '#6B778C'),
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}
          >
            - {option.summary}
          </span>
        )}
      </div>
    )
  }, [])

  /**
   * NO OPTIONS MESSAGE
   * Shown when search returns no results.
   */
  const noOptionsMessage = useCallback(({ inputValue }) => {
    if (!inputValue || inputValue.length < 2) {
      return 'Type at least 2 characters to search'
    }
    if (hasSearched) {
      return `No issues found for "${inputValue}"`
    }
    return 'Start typing to search issues'
  }, [hasSearched])

  return (
    <AsyncSelect
      value={value}
      onChange={handleChange}
      loadOptions={loadOptions}
      formatOptionLabel={formatOptionLabel}
      noOptionsMessage={noOptionsMessage}
      placeholder={placeholder}
      isDisabled={isDisabled}
      isClearable
      cacheOptions
      defaultOptions={false}
      loadingMessage={() => 'Searching issues...'}
      styles={{
        // Ensure dropdown renders above other elements
        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
        // Custom option styling for better padding
        option: (base, state) => ({
          ...base,
          padding: '8px 12px',
          backgroundColor: state.isFocused
            ? token('color.background.neutral.hovered', '#F4F5F7')
            : 'transparent',
          cursor: 'pointer'
        }),
        // Ensure proper height for the control
        control: (base) => ({
          ...base,
          minHeight: '40px'
        })
      }}
      menuPortalTarget={document.body}
    />
  )
}

export default JiraIssuePicker
